import { type Components as MuiComponents, createTheme, type PaletteMode } from '@mui/material'
import { deepmerge } from '@mui/utils'
import type { ThemeOption, Tokens } from '../augmentation'
import { getBaseUnit } from './helpers/get-base-unit'
// please try to keep these on alphabet for reading
import { createMuiAlert } from './muiAlert'
import { createMuiAutocomplete } from './muiAutocomplete'
import { createMuiAvatar } from './muiAvatar'
import { createMuiBackdrop } from './muiBackdrop'
import { createMuiBadge } from './muiBadge'
import { createMuiBottomNavigation } from './muiBottomNavigation'
import { createMuiButton } from './muiButton'
import { createMuiButtonBase } from './muiButtonBase'
import { createMuiButtonGroup } from './muiButtonGroup'
import { createMuiCard } from './muiCard'
import { createMuiCheckbox } from './muiCheckbox'
import { createMuiCircularProgress } from './muiCircularProgress'
import { createMuiDataGrid } from './muiDataGrid'
import { createMuiDivider } from './muiDivider'
import { createMuiDrawer } from './muiDrawer'
import { createMuiFab } from './muiFab'
import { createMuiFormControl } from './muiFormControl'
import { createMuiFormGroup } from './muiFormGroup'
import { createMuiFormHelperText } from './muiFormHelperText'
import { createMuiFormLabel } from './muiFormLabel'
import { createMuiIconButton } from './muiIconButton'
import { createMuiInput } from './muiInput'
import { createMuiLink } from './muiLink'
import { createMuiList } from './muiList'
import { createMuiMenuItem } from './muiMenuItem'
import { createMuiModal } from './muiModal'
import { createMuiPaper } from './muiPaper'
import { createMuiPopper } from './muiPopper'
import { createMuiRadio } from './muiRadio'
import { createMuiSelect } from './muiSelect'
import { createMuiSkeleton } from './muiSkeleton'
import { createMuiSwitch } from './muiSwitch'
import { muiSvgIcon } from './muiSvgIcon'
import { createMuiTab } from './muiTab'
import { createMuiTable } from './muiTable'
import { createMuiTabs } from './muiTabs'
import { createMuiToggleButton } from './muiToggleButton'
import { createMuiToggleButtonGroup } from './muiToggleButtonGroup'
import { createMuiToolbar } from './muiToolbar'
import { createMuiTooltip } from './muiTooltip'
import { createMuixCalendar } from './muixCalendar'
import { createMuiCssbaseline } from './muiCssBaseline'

const lightModeComponents = (
  { palette, typography, variant, misc, elevation }: Tokens,
  options?: Omit<ThemeOption, 'variant'>
): MuiComponents => {
  const fontSize = getBaseUnit(variant)

  const { components } = createTheme({
    components: {
      ...createMuiCssbaseline({
        palette,
        fontSize,
        userSelect: options?.userSelect,
        disablePtr: options?.disablePtr ?? false,
      }),
      // Our components
      ...createMuiAlert({ palette, misc }),
      ...createMuiAutocomplete({ elevation }),
      ...createMuiAvatar({ palette }),
      ...createMuiBackdrop({ palette }),
      ...createMuiBadge({ palette, misc, typography }),
      ...createMuiBottomNavigation({ palette, misc }),
      ...createMuiButton({ palette, misc }),
      ...createMuiButtonBase(),
      ...createMuiButtonGroup({ misc }),
      ...createMuiCard(),
      ...createMuiCheckbox({ palette, misc }),
      ...createMuiCircularProgress(),
      ...createMuiDataGrid({ palette, typography }),
      ...createMuiDivider({ misc }),
      ...createMuiDrawer({ palette }),
      ...createMuiFab({ palette, misc, elevation }),
      ...createMuiFormGroup(),
      ...createMuiFormHelperText({ palette }),
      ...createMuiFormControl({ palette }),
      ...createMuiFormLabel({ palette }),
      ...createMuiIconButton({ palette, misc }),
      ...createMuiInput({ palette, misc }),
      ...createMuiLink({ palette, misc }),
      ...createMuiList({ palette }),
      ...createMuiMenuItem({ palette }),
      ...createMuiModal({ variant, misc }),
      ...createMuiPaper({ palette }),
      ...createMuiPopper({ misc }),
      ...createMuiRadio({ misc }),
      ...createMuiSelect({ palette, misc }),
      ...createMuiSkeleton(),
      ...muiSvgIcon,
      ...createMuiSwitch({ palette, misc }),
      ...createMuiTab({ palette, misc }),
      ...createMuiTable({ palette, typography }),
      ...createMuiTabs({ palette }),
      ...createMuiToggleButton({ palette }),
      ...createMuiToggleButtonGroup({ palette }),
      ...createMuiToolbar(),
      ...createMuiTooltip({ palette }),
      ...createMuixCalendar({ palette, typography }),
    },
  })

  return components!
}

// in future we can pass the tokens!
const darkModeComponents = (): MuiComponents => {
  const { components } = createTheme()

  return components!
}

export const createComponents = (
  tokens: Tokens,
  paletteMode: PaletteMode,
  options?: Omit<ThemeOption, 'variant'>
): MuiComponents => {
  const light = lightModeComponents(tokens, options)

  if (paletteMode === 'light') {
    return light
  }

  return deepmerge(light, darkModeComponents())
}
